import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useToast } from '@eluve/components';
import { UserFileTypesLookup } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { calculateFileMd5 } from '@eluve/utils';

import { UploadedFile } from './types';

interface UseUploadPdfProps {
  tenantId: string;
  threadId?: string;
  onUploadComplete?: (file: UploadedFile) => void;
  onPasswordRequired?: (file: UploadedFile) => void;
  sizeLimit?: number;
}

const ONE_MB = 1024 * 1024;
const DEFAULT_PDF_SIZE_LIMIT = 10 * ONE_MB;

export const useUploadPdf = ({
  tenantId,
  threadId,
  onUploadComplete,
  sizeLimit = DEFAULT_PDF_SIZE_LIMIT,
}: UseUploadPdfProps) => {
  const toast = useToast();
  const userId = useUserIdFromSession();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const logger = useNamedLogger('useUploadPdf');
  const apiClient = useApiClient();

  const uploadUserFileToGCS = async (file: File): Promise<string | null> => {
    const fileName = file.name;
    const fileSize = file.size;
    const fileHash = await calculateFileMd5(file);

    const prepareUserFileUploadResponse =
      await apiClient.users.prepareUserFileUpload({
        params: { tenantId },
        body: {
          extension: 'pdf',
          fileType: UserFileTypesLookup.CHAT_DOCUMENT,
          fileName,
          fileSize,
          fileHash,
        },
      });

    if (prepareUserFileUploadResponse.status !== 200) {
      logger.error('Failed to prepare user file upload');
      return null;
    }

    const { uploadUrl, userUploadId } = prepareUserFileUploadResponse.body;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: file,
    });

    if (!uploadResponse.ok) {
      await apiClient.users.markUserFileUploadFailed({
        params: { tenantId },
        body: { userUploadId },
      });
      return null;
    }

    await apiClient.users.markUserFileUploadComplete({
      params: { tenantId },
      body: { userUploadId },
    });

    return userUploadId;
  };

  const uploadFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > sizeLimit) {
      toast.error(`File size must be less than ${sizeLimit / ONE_MB}MB`);
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      const userUploadId = await uploadUserFileToGCS(file);
      if (!userUploadId) {
        throw new Error('Failed to upload file to GCS');
      }

      let fileMetadata: { hasPassword?: boolean } = {};
      let presignedUrl: string | undefined;

      if (threadId) {
        // Add file to thread with automatic password detection
        const addFileResponse = await apiClient.chat.addFileToThread({
          params: { tenantId, threadId, fileId: userUploadId },
        });

        if (addFileResponse.status !== 200) {
          throw new Error('Failed to add file to thread');
        }

        fileMetadata = addFileResponse.body.fileMetadata;

        const getFileResponse = await apiClient.users.getUserFileSignedUrl({
          params: { tenantId, userId, fileId: userUploadId },
        });

        if (getFileResponse.status !== 200) {
          throw new Error('Failed to get file signed URL');
        }

        presignedUrl = getFileResponse.body.presignedUrl ?? undefined;
      }

      const uploadedFile: UploadedFile = {
        id: userUploadId,
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
        fileUrl: presignedUrl,
        fileSize: file.size,
        hasPassword: fileMetadata.hasPassword,
      };

      onUploadComplete?.(uploadedFile);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed';
      setUploadError(errorMessage);
      toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFile,
    isUploading,
    uploadError,
  };
};
